/**
 * Tavern-phase ability resolution.
 *
 * The ability engine (abilityEngine.ts) is pure: it maps a trigger on a
 * board unit to a list of AbilityResults. Combat consumes those results
 * inside combatEngine; this module is the tavern-side consumer, so
 * "Summon:" battlecries, on-buy/on-sell hooks and end-of-turn effects
 * actually do something when the player is in the shop.
 *
 * All state goes in and out immutably so the match store can spread the
 * result straight into set().
 */

import type { Card, AbilityTrigger, TribeId } from './data/cards/types';
import { tokens, cardsByTier } from './data/cards';
import { newInstance } from './shop';
import { triggerAbilities, type CardInstance, type TriggerContext } from './abilityEngine';
import { applyPlaqueTag } from './plaque';
import { resolvePermanentBuff, buffApplications } from './buffs';

// Hero health cap — mirrors STARTING_HEALTH in the match store (not
// imported from there to keep game/ free of store dependencies).
export const MAX_HERO_HEALTH = 30;
export const HAND_LIMIT = 10;

/** A Discover the store still has to present to the player. */
export interface PendingDiscover {
  tier: number;
  tribe?: TribeId;
  /** Name of the unit that asked for it — drives the modal's eyebrow copy. */
  sourceName: string;
}

export interface TavernAbilityState {
  board: (Card | null)[];
  hand: Card[];
  shop: Card[];
  gold: number;
  health: number;
  /**
   * Set when a resolved ability wants the Discover modal opened. The store
   * owns the actual card roll and the modal state; this module only reports
   * that one is owed. Only the FIRST discover of a trigger batch is kept —
   * two modals cannot be open at once, and no card stacks them today.
   */
  pendingDiscover?: PendingDiscover | null;
  /** Gold owed off the opponent's stack (La Karen). Reported, not applied. */
  opponentGoldDrain?: number;
}

/**
 * Facts about the wider turn that some abilities read. Passed straight
 * through to TriggerContext — the store owns these counters, this module
 * just forwards them.
 */
export interface TavernTriggerFacts {
  buysThisTurn?: number;
  playsThisTurn?: number;
  roundNumber?: number;
  heroPowerBuffsUnits?: boolean;
}

/**
 * Fires `trigger` for each board slot in `sourceSlots` (in order) and
 * applies the resulting effects to the tavern state.
 *
 * - `buff` → permanent stat change on the board unit
 * - `combat_buff` → stashed on card.combatBuff; folded in at combat sim
 * - `buff_shop` → permanent stat change on a random current shop card
 * - `gain_gold` / `heal_hero` → player resources (health capped)
 * - `summon` → token placed in the nearest free slot at/after position
 * - `add_card` → random shop-pool card of that tier added to hand
 * - `discover` → reported via `pendingDiscover` for the store to present
 */
export function fireTavernTrigger(
  trigger: AbilityTrigger,
  sourceSlots: number[],
  state: TavernAbilityState,
  rng: () => number = Math.random,
  facts: TavernTriggerFacts = {},
): TavernAbilityState {
  const board = [...state.board];
  const hand = [...state.hand];
  const shop = [...state.shop];
  let gold = state.gold;
  let health = state.health;
  let pendingDiscover: PendingDiscover | null = state.pendingDiscover ?? null;
  let opponentGoldDrain = state.opponentGoldDrain ?? 0;

  for (const slot of sourceSlots) {
    const source = board[slot];
    if (!source || !source.abilities) continue;

    const context: TriggerContext = {
      owner: 'player',
      shop: shop as CardInstance[],
      ...facts,
    };
    const results = triggerAbilities(trigger, slot, board as CardInstance[], context);

    for (const result of results) {
      switch (result.type) {
        case 'buff': {
          // Permanent buffs go through the amplifier/watcher resolver so
          // Coleccionista de Metal doubles what it receives and Muralista
          // notices that someone got bigger.
          const resolved = resolvePermanentBuff(
            board,
            result.targetIndex,
            result.atkDelta,
            result.hpDelta,
            rng,
          );
          for (const app of buffApplications(resolved)) {
            const target = board[app.targetIndex];
            if (!target) continue;
            board[app.targetIndex] = {
              ...target,
              attack: target.attack + app.atk,
              health: target.health + app.hp,
            };
          }
          break;
        }
        case 'combat_buff': {
          const target = board[result.targetIndex];
          if (target) {
            const prev = target.combatBuff ?? { attack: 0, health: 0 };
            board[result.targetIndex] = {
              ...target,
              combatBuff: {
                attack: prev.attack + result.atkDelta,
                health: prev.health + result.hpDelta,
              },
            };
          }
          break;
        }
        case 'plaque_tag': {
          const target = board[result.targetIndex];
          if (target) {
            const tagged = { ...target };
            applyPlaqueTag(tagged, result.count);
            board[result.targetIndex] = tagged;
          }
          break;
        }
        case 'buff_shop': {
          if (shop.length > 0) {
            const idx = Math.floor(rng() * shop.length);
            const target = shop[idx];
            shop[idx] = {
              ...target,
              attack: target.attack + result.atkDelta,
              health: target.health + result.hpDelta,
            };
          }
          break;
        }
        case 'gain_gold':
          gold += result.amount;
          break;
        case 'heal_hero':
          health = Math.min(MAX_HERO_HEALTH, health + result.amount);
          break;
        case 'summon': {
          const token = tokens.find((t) => t.id === result.cardId);
          if (token) {
            // Prefer a free slot at/after the summoner, else any free slot.
            let free = board.findIndex(
              (s, i) => s === null && i >= result.position,
            );
            if (free < 0) free = board.findIndex((s) => s === null);
            if (free >= 0) board[free] = newInstance(token);
          }
          break;
        }
        case 'add_card': {
          if (hand.length < HAND_LIMIT) {
            const pool = cardsByTier[result.tier] ?? [];
            if (pool.length > 0) {
              const pick = pool[Math.floor(rng() * pool.length)];
              hand.push(newInstance(pick));
            }
          }
          break;
        }
        case 'enemy_debuff': {
          // Parked on the summoner. simulateCombat spends it against the
          // other board, and the round commit clears it — same lifecycle
          // as combatBuff.
          const target = board[slot];
          if (target) {
            const prev = target.enemyDebuff ?? { attack: 0, health: 0 };
            board[slot] = {
              ...target,
              enemyDebuff: {
                attack: prev.attack + result.atkDelta,
                health: prev.health + result.hpDelta,
              },
            };
          }
          break;
        }
        case 'swap_positions': {
          const { a, b } = result;
          if (board[a] && board[b]) {
            const tmp = board[a];
            board[a] = board[b];
            board[b] = tmp;
          }
          break;
        }
        case 'drain_opponent_gold': {
          opponentGoldDrain += result.amount;
          break;
        }
        case 'discover': {
          // Report it upward; the store rolls the three options and opens
          // the modal. First one wins (see PendingDiscover).
          if (!pendingDiscover) {
            pendingDiscover = {
              tier: result.tier,
              tribe: result.tribe,
              sourceName: source.name,
            };
          }
          break;
        }
        default:
          // 'log' results (unimplemented ability ids) are silently skipped
          // in the tavern; combat logs them via its own event stream.
          break;
      }
    }
  }

  return { board, hand, shop, gold, health, pendingDiscover, opponentGoldDrain };
}

/** Indices of occupied board slots — the usual sourceSlots argument. */
export function occupiedSlots(board: (Card | null)[]): number[] {
  const slots: number[] = [];
  for (let i = 0; i < board.length; i++) {
    if (board[i]) slots.push(i);
  }
  return slots;
}

/** Strip transient combat-only buffs and debuffs after a round commits. */
export function clearCombatBuffs(board: (Card | null)[]): (Card | null)[] {
  return board.map((card) =>
    card && (card.combatBuff || card.enemyDebuff)
      ? { ...card, combatBuff: undefined, enemyDebuff: undefined }
      : card,
  );
}
